import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut, Settings as SettingsIcon, ChevronUp, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Sleek glass pill shared by every item in the account stack. */
const itemClass =
  "h-9 gap-1.5 rounded-full border border-border/60 bg-background/70 px-3 text-[13px] font-medium text-foreground/90 shadow-sm backdrop-blur-xl transition-colors hover:bg-background/90";


export default function DashboardFab() {
  const { user, loading, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (loading || !user) return null;
  if (pathname === "/auth") return null;
  // Shell pages carry the same links in the You hub — no second account menu.
  if (["/home", "/careers", "/next-steps", "/report", "/command-center", "/dashboard"].includes(pathname) || pathname.startsWith("/hub/")) return null;
  if (!visible) return null;

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  return (
    // Dock slot 1 — the base of the floating column, above the mobile tab bar.
    <div className="fixed bottom-[92px] right-4 z-[60] print:hidden flex flex-col items-end gap-1.5 lg:bottom-6">
      {open && (
        <div className="flex flex-col items-end gap-1.5 animate-in fade-in slide-in-from-bottom-2">
          {pathname !== "/dashboard" && (
            <Button asChild variant="ghost" className={itemClass}>
              <Link to="/dashboard" onClick={() => setOpen(false)}>
                <LayoutDashboard className="h-3.5 w-3.5" aria-hidden="true" /> Dashboard
              </Link>
            </Button>
          )}
          {isAdmin && (
            <Button asChild variant="ghost" className={itemClass}>
              <Link to="/admin" onClick={() => setOpen(false)}>
                <Shield className="h-3.5 w-3.5" aria-hidden="true" /> Admin
              </Link>
            </Button>
          )}
          <Button asChild variant="ghost" className={itemClass}>
            <Link to="/settings" onClick={() => setOpen(false)}>
              <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" /> Settings
            </Link>
          </Button>

          <Button variant="ghost" className={itemClass} onClick={handleSignOut}>
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out
          </Button>
        </div>
      )}
      <Button
        aria-expanded={open}
        aria-label={open ? "Close account menu" : "Open account menu"}
        onClick={() => setOpen((v) => !v)}
        className="h-9 gap-1.5 rounded-full bg-primary px-3 text-[13px] font-medium shadow-md shadow-primary/20 transition-transform hover:scale-105 active:scale-95"
      >
        <ChevronUp
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
        <span>Account</span>
      </Button>
    </div>
  );
}

